/**
 * LabelPreview — renders a single label as an <svg> pre-scaled to the
 * template's mm dimensions. The SAME SVG string is:
 *   - Shown in the live preview inside PrintLabelModal.
 *   - Rasterised to a canvas → sent to the Niimbot over Bluetooth.
 *   - Streamed to the print iframe for the browser-print fallback.
 *
 * We use SVG (not HTML+CSS) because it scales pixel-perfect at any DPI —
 * critical for thermal printers where 203 vs 300 dpi shows on the receipt.
 *
 * Two rendering modes:
 *   - GENERIC (default) — brand header + rows on the left, QR/barcode on
 *     the right. Used by Gemstone Label, Jewelry Box, Warehouse, custom.
 *   - RING-TAG ZONED — kicks in when `template.kind === "ring_tag"`. Splits
 *     the physical 75 × 25 mm Niimbot jewelry paper into its three die-cut
 *     zones (15 + 15 + 45 mm) so content never straddles a perforation. See
 *     `renderRingTagZoned()` for the exact layout.
 *
 * Props:
 *   product        — the inventory item (name, sku, dimension, ring_size, metal,
 *                    selling_price, qr_short_id ...)
 *   template       — LabelTemplate from /api/labels/templates
 *   language       — "en" | "id"
 *   showQR         — override template.show_qr (modal toggle)
 *   showBarcode    — override template.show_barcode (modal toggle)
 *   fontSizeOverride — override template.font_size_pt (modal slider)
 *   qrDataUrl      — pre-rendered QR PNG data URL (parent handles qrcode lib)
 *   barcodeDataUrl — pre-rendered Code128 SVG string (parent handles jsbarcode)
 *   showPerforations — preview-only: dashed lines between ring-tag zones.
 */
import { LABEL_STRINGS, buildLabelRows, formatIDR, pickField } from "../lib/labelFormat";

const MM_TO_PX = 3.7795275591; // 96 dpi CSS pixel → 1 mm

// -------- Ring-tag zone geometry (mm) --------
// Zone 1 (brand + SKU) | Zone 2 (price + weight) | Zone 3 tail (QR + name)
const RING_TAG_ZONES = { z1: 15, z2: 15, z3: 45 };

function isRingTag(template) {
  // Zoned layout is retired — user prefers the generic look. Kept as a
  // guard so the shared `ringTagPrimitives()` helper stays available for
  // any future opt-in template (e.g. `template.kind === "ring_tag_zoned"`).
  return template?.kind === "ring_tag_zoned";
}

/** True when the template was built with the drag-and-drop designer — we
 *  render from the raw element list instead of the generic header+rows
 *  layout so the shop's custom design lands identically on-screen and on
 *  the Niimbot output. */
function hasCustomLayout(template) {
  return Array.isArray(template?.extras?.layout?.elements)
    && template.extras.layout.elements.length > 0;
}


// =========================================================================
// CUSTOM LAYOUT (from drag-and-drop designer)
// =========================================================================

/**
 * Turn a designer-saved layout (mm coordinates, pt font sizes) into the
 * same primitive list used by the ring-tag path. Coordinates are in mm,
 * output is in target-space pixels controlled by `unit`:
 *   unit=scale (React preview) → CSS pixels at that zoom.
 *   unit=1 (print SVG builder) → real 1 mm = 3.78 px pixels for rasterisation.
 * `pt → target-px` uses the same MM_TO_PX * unit factor so text scales in
 * lockstep with position (a 8 pt label on a 25 mm high tag stays 8 pt).
 */
function customLayoutPrimitives(product, template, language, opts) {
  const { unit, showQR, showBarcode, qrDataUrl, barcodeDataUrl } = opts;
  const layout = template.extras.layout;
  const els = layout.elements || [];

  const family = (kind) =>
    kind === "serif" ? "'Cormorant Garamond', 'Times New Roman', serif"
      : kind === "mono" ? "'Menlo', monospace"
      : "'Manrope', 'Arial', sans-serif";

  const pxFromMm = (mm) => mm * MM_TO_PX * unit;

  const primitives = [];
  for (const el of els) {
    const xPx = pxFromMm(el.x);
    const yPx = pxFromMm(el.y);
    const wPx = pxFromMm(el.w || 0);
    const hPx = pxFromMm(el.h || 0);

    if (el.type === "text" || el.type === "field") {
      // Text baseline sits `size * 0.9` down from y=top so the box the
      // designer sees matches what the reader sees on the print.
      const size = (el.size || 8) * unit;
      const value = el.type === "field"
        ? (el.field === "price" ? formatIDR(product.selling_price)
            : el.field === "carat" ? (product.carat ? `${Number(product.carat).toFixed(2)} ct` : "")
            : pickField(product, el.field))
        : (el.text || "");
      if (!value) continue;
      // Alignment anchors — matches designer preview exactly.
      const anchor = el.align === "center" ? "middle" : el.align === "right" ? "end" : "start";
      const x = el.align === "center" ? xPx + wPx / 2
        : el.align === "right" ? xPx + wPx : xPx;
      primitives.push({
        kind: "text",
        x, y: yPx + size * 0.9,
        anchor,
        family: family(el.family),
        size,
        weight: el.weight || 400,
        fill: el.color || "#000",
        text: value,
      });
    } else if (el.type === "qr") {
      if (showQR && qrDataUrl) {
        primitives.push({
          kind: "image", x: xPx, y: yPx, w: wPx || hPx, h: hPx || wPx,
          href: qrDataUrl,
        });
      }
    } else if (el.type === "barcode") {
      if (showBarcode && barcodeDataUrl) {
        // Barcode SVG is delivered as an <svg> string; splice it inside a
        // <g transform="translate(...) scale(...)"> so it lands in the box
        // the designer drew. Height scales to h; width scales isotropically.
        primitives.push({
          kind: "barcode_group",
          x: xPx, y: yPx, w: wPx, h: hPx,
          inner: barcodeDataUrl,
        });
      }
    } else if (el.type === "line") {
      // Horizontal divider — h ~ stroke width (in mm), w = length (mm).
      primitives.push({
        kind: "line",
        x1: xPx, y1: yPx, x2: xPx + wPx, y2: yPx,
        stroke: el.color || "#D4AF37",
        strokeWidth: Math.max(1, hPx || 2),
      });
    }
  }
  return primitives;
}


// =========================================================================
// SHARED HELPERS
// =========================================================================

function escapeXml(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}


// =========================================================================
// RING-TAG ZONED LAYOUT
// =========================================================================

/**
 * Compute the render primitives (positions + text) for a ring tag broken
 * into its 3 die-cut zones. Returned array is [ { kind, ... }, ... ] and
 * consumed by both the React JSX renderer and the string SVG builder.
 *
 * Layout at 75 × 25 mm:
 *   Zone 1 (0-15 mm):  brand + SKU
 *   Zone 2 (15-30 mm): price (large) + carat/weight (small)
 *   Zone 3 (30-75 mm): QR (left of tail) + product name + dimension
 */
function ringTagPrimitives(product, template, language, opts) {
  const t = LABEL_STRINGS[language] || LABEL_STRINGS.en;
  const { unit, showQR, fontSize } = opts;

  const wMm = template.width_mm || 75;
  const hMm = template.height_mm || 25;
  const wPx = wMm * MM_TO_PX * unit;
  const hPx = hMm * MM_TO_PX * unit;

  // Zone bounds in px
  const z1End = RING_TAG_ZONES.z1 * MM_TO_PX * unit;
  const z2End = (RING_TAG_ZONES.z1 + RING_TAG_ZONES.z2) * MM_TO_PX * unit;
  // z3 spans z2End → wPx
  const pad = 1.2 * MM_TO_PX * unit;

  // Fonts (pt → px * unit). Ring tag fonts stay small because the zones
  // are narrow — we keep body around fontSize, brand ~1.3×.
  const brandFont = Math.max(6, fontSize * 1.15) * unit;
  const skuFont = Math.max(5, fontSize * 0.82) * unit;
  const priceFont = Math.max(9, fontSize * 1.55) * unit;
  const smallFont = Math.max(5, fontSize * 0.75) * unit;
  const nameFont = Math.max(6, fontSize * 0.95) * unit;

  const primitives = [];

  // -------- Zone 1: OPIK JEWELS + SKU --------
  primitives.push({
    kind: "text",
    x: z1End / 2,
    y: pad + brandFont * 0.9,
    anchor: "middle",
    family: "'Cormorant Garamond', 'Times New Roman', serif",
    size: brandFont,
    weight: 700,
    letterSpacing: brandFont * 0.08,
    fill: "#000",
    text: t.header,
  });
  const sku = pickField(product, "sku");
  if (sku) {
    primitives.push({
      kind: "text",
      x: z1End / 2,
      y: hPx / 2 + brandFont * 0.5,
      anchor: "middle",
      family: "'Manrope', 'Arial', sans-serif",
      size: skuFont,
      weight: 500,
      fill: "#333",
      text: sku,
    });
  }
  // Hairline gold underline in zone 1 to echo the certificate feel
  primitives.push({
    kind: "line",
    x1: pad, y1: brandFont * 1.5 + pad, x2: z1End - pad, y2: brandFont * 1.5 + pad,
    stroke: "#D4AF37", strokeWidth: 0.3 * MM_TO_PX * unit,
  });

  // -------- Zone 2: Price + Weight --------
  const price = product.selling_price;
  if (price) {
    // Price broken into "Rp" + amount so we can lay them out cleanly on
    // a narrow tab.
    const priceText = formatIDR(price).replace("Rp ", "");
    primitives.push({
      kind: "text",
      x: z1End + (RING_TAG_ZONES.z2 * MM_TO_PX * unit) / 2,
      y: pad + smallFont * 0.85,
      anchor: "middle",
      family: "'Manrope', sans-serif",
      size: smallFont,
      weight: 500,
      fill: "#7BA585",
      text: t.price?.toUpperCase() || "PRICE",
    });
    primitives.push({
      kind: "text",
      x: z1End + (RING_TAG_ZONES.z2 * MM_TO_PX * unit) / 2,
      y: hPx / 2 + priceFont * 0.15,
      anchor: "middle",
      family: "'Manrope', sans-serif",
      size: priceFont,
      weight: 700,
      fill: "#000",
      text: `Rp ${priceText}`,
    });
  }
  // Weight (carat) — tucked at the bottom of zone 2
  const carat = product.carat || (product.quantity && product.unit === "ct" ? product.quantity : "");
  const weightStr = carat ? `${Number(carat).toFixed(2)} ct` : "";
  if (weightStr) {
    primitives.push({
      kind: "text",
      x: z1End + (RING_TAG_ZONES.z2 * MM_TO_PX * unit) / 2,
      y: hPx - pad,
      anchor: "middle",
      family: "'Manrope', sans-serif",
      size: smallFont,
      weight: 600,
      fill: "#333",
      text: weightStr,
    });
  }

  // -------- Zone 3 (the tail): QR + product name + dimension --------
  const z3Left = z2End + pad;
  const qrSize = Math.min(hPx - pad * 2, (wPx - z3Left) * 0.4);
  if (showQR) {
    primitives.push({
      kind: "image",
      x: z3Left,
      y: (hPx - qrSize) / 2,
      w: qrSize,
      h: qrSize,
      href: opts.qrDataUrl,
    });
  }
  // Text block right of QR
  const textLeft = showQR ? z3Left + qrSize + pad * 0.6 : z3Left;
  const textWidth = wPx - textLeft - pad;
  const nameStr = pickField(product, "name");
  if (nameStr) {
    // Trim so it fits the narrow tail
    const maxChars = Math.max(8, Math.floor(textWidth / (nameFont * 0.5)));
    const truncated = nameStr.length > maxChars ? nameStr.slice(0, maxChars - 1) + "…" : nameStr;
    primitives.push({
      kind: "text",
      x: textLeft,
      y: pad + nameFont * 1.1,
      family: "'Cormorant Garamond', serif",
      size: nameFont,
      weight: 700,
      fill: "#000",
      text: truncated,
    });
  }
  const dim = pickField(product, "dimension");
  if (dim) {
    primitives.push({
      kind: "text",
      x: textLeft,
      y: pad + nameFont * 1.1 + smallFont * 1.6,
      family: "'Manrope', sans-serif",
      size: smallFont,
      weight: 500,
      fill: "#555",
      text: dim,
    });
  }
  // "SCAN TO VERIFY" hint below the text block if we have QR
  if (showQR) {
    primitives.push({
      kind: "text",
      x: textLeft,
      y: hPx - pad,
      family: "'Manrope', sans-serif",
      size: smallFont * 0.85,
      weight: 500,
      fill: "#7BA585",
      text: (t.scan_to_view || "Scan to verify").toUpperCase(),
    });
  }

  return { primitives, zones: { z1End, z2End, wPx, hPx } };
}


// =========================================================================
// REACT PREVIEW COMPONENT
// =========================================================================

export function LabelPreview({
  product,
  template,
  language = "en",
  showQR,
  showBarcode,
  fontSizeOverride,
  qrDataUrl,
  barcodeDataUrl,
  scale = 4,
  showPerforations = true,
}) {
  const t = LABEL_STRINGS[language] || LABEL_STRINGS.en;
  const rows = buildLabelRows(product, template, language);

  const effShowQR = showQR ?? template.show_qr;
  const effShowBarcode = showBarcode ?? template.show_barcode;
  const fontSize = fontSizeOverride ?? template.font_size_pt ?? 8;

  const wMm = template.width_mm;
  const hMm = template.height_mm;
  const wPx = wMm * MM_TO_PX * scale;
  const hPx = hMm * MM_TO_PX * scale;

  // -------- CUSTOM LAYOUT (designer output) --------
  // Highest priority: if the operator saved a drag-and-drop layout it
  // overrides both the generic layout and the ring-tag zoned path.
  if (hasCustomLayout(template)) {
    const primitives = customLayoutPrimitives(product, template, language, {
      unit: scale,
      showQR: effShowQR,
      showBarcode: effShowBarcode,
      qrDataUrl,
      barcodeDataUrl,
    });
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`0 0 ${wPx} ${hPx}`}
        width={wPx}
        height={hPx}
        style={{ background: "#FFFFFF", boxShadow: "0 0 0 1px #D4AF37" }}
        data-testid="label-preview-svg"
      >
        <rect x="0" y="0" width={wPx} height={hPx} fill="#FFFFFF" />
        {primitives.map((p, i) => renderPrimReact(p, i))}
      </svg>
    );
  }

  // -------- RING-TAG ZONED render --------
  if (isRingTag(template)) {
    const { primitives, zones } = ringTagPrimitives(product, template, language, {
      unit: scale, showQR: effShowQR, fontSize, qrDataUrl,
    });
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`0 0 ${wPx} ${hPx}`}
        width={wPx}
        height={hPx}
        style={{ background: "#FFFFFF", boxShadow: "0 0 0 1px #D4AF37" }}
        data-testid="label-preview-svg"
      >
        <rect x="0" y="0" width={wPx} height={hPx} fill="#FFFFFF" />
        {/* Preview-only dashed perforation lines so the operator can see
            exactly where the die-cut sits on the physical paper.        */}
        {showPerforations && (
          <>
            <line x1={zones.z1End} y1={0} x2={zones.z1End} y2={hPx}
                  stroke="#D4AF37" strokeDasharray="3,4" strokeOpacity="0.55" />
            <line x1={zones.z2End} y1={0} x2={zones.z2End} y2={hPx}
                  stroke="#D4AF37" strokeDasharray="3,4" strokeOpacity="0.55" />
          </>
        )}
        {primitives.map((p, i) => renderPrimReact(p, i))}
      </svg>
    );
  }

  // -------- GENERIC label render (Gemstone, Jewelry Box, Warehouse) --------
  const padPx = 1.5 * MM_TO_PX * scale;
  const headerFont = Math.round(fontSize * scale * 1.4);
  const rowFont = Math.round(fontSize * scale);
  const qrSizePx = Math.min(
    hPx - padPx * 3 - headerFont - rows.length * rowFont * 1.2,
    (wPx - padPx * 2) * 0.32,
  );

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${wPx} ${hPx}`}
      width={wPx}
      height={hPx}
      style={{ background: "#FFFFFF", boxShadow: "0 0 0 1px #D4AF37" }}
      data-testid="label-preview-svg"
    >
      <rect x="0" y="0" width={wPx} height={hPx} fill="#FFFFFF" />
      <text
        x={padPx}
        y={padPx + headerFont * 0.85}
        fontFamily="'Cormorant Garamond', 'Times New Roman', serif"
        fontSize={headerFont}
        fontWeight="700"
        letterSpacing={headerFont * 0.06}
        fill="#000000"
      >
        {`${t.header}`}
      </text>
      {rows.map((row, idx) => {
        const y = padPx + headerFont * 1.25 + (idx + 1) * rowFont * 1.4;
        return (
          <g key={idx}>
            <text x={padPx} y={y} fontFamily="'Manrope', 'Arial', sans-serif"
                  fontSize={rowFont * 0.85} fontWeight="500" fill="#333333">
              {`${row.label}:`}
            </text>
            <text x={padPx + rowFont * 4.2} y={y} fontFamily="'Manrope', 'Arial', sans-serif"
                  fontSize={rowFont} fontWeight="700" fill="#000000">
              {`${row.value}`}
            </text>
          </g>
        );
      })}
      {effShowQR && qrDataUrl && !effShowBarcode && (
        <image href={qrDataUrl} x={wPx - padPx - qrSizePx} y={padPx + headerFont * 0.5}
               width={qrSizePx} height={qrSizePx} />
      )}
      {effShowBarcode && barcodeDataUrl && (
        <g transform={`translate(${padPx}, ${hPx - padPx - rowFont * 2.5})`}
           dangerouslySetInnerHTML={{ __html: barcodeDataUrl }} />
      )}
    </svg>
  );
}

function renderPrimReact(p, key) {
  if (p.kind === "line") {
    return (
      <line key={key} x1={p.x1} y1={p.y1} x2={p.x2} y2={p.y2}
            stroke={p.stroke} strokeWidth={p.strokeWidth} />
    );
  }
  if (p.kind === "image") {
    if (!p.href) return null;
    return <image key={key} href={p.href} x={p.x} y={p.y} width={p.w} height={p.h} />;
  }
  if (p.kind === "barcode_group") {
    if (!p.inner) return null;
    // Inline the pre-rendered barcode SVG (jsbarcode output) at the box the
    // designer drew. Barcode SVGs come with their own width; we scale the
    // group so it fits the designer's WxH box exactly.
    return (
      <g key={key} transform={`translate(${p.x}, ${p.y})`}
         dangerouslySetInnerHTML={{ __html: p.inner }} />
    );
  }
  // text
  return (
    <text key={key}
          x={p.x} y={p.y}
          textAnchor={p.anchor || "start"}
          fontFamily={p.family}
          fontSize={p.size}
          fontWeight={p.weight || 400}
          letterSpacing={p.letterSpacing || 0}
          fill={p.fill}>
      {`${p.text}`}
    </text>
  );
}


// =========================================================================
// STRING SVG BUILDER (used by print + Bluetooth pipelines)
// =========================================================================

export function renderLabelSVG({
  product,
  template,
  language = "en",
  showQR,
  showBarcode,
  fontSizeOverride,
  qrDataUrl,
  barcodeDataUrl,
}) {
  const t = LABEL_STRINGS[language] || LABEL_STRINGS.en;
  const rows = buildLabelRows(product, template, language);
  const effShowQR = showQR ?? template.show_qr;
  const effShowBarcode = showBarcode ?? template.show_barcode;
  const fontSize = fontSizeOverride ?? template.font_size_pt ?? 8;

  const wMm = template.width_mm;
  const hMm = template.height_mm;
  const wPx = wMm * MM_TO_PX;
  const hPx = hMm * MM_TO_PX;

  // -------- CUSTOM LAYOUT (designer output) --------
  if (hasCustomLayout(template)) {
    const primitives = customLayoutPrimitives(product, template, language, {
      unit: 1,
      showQR: effShowQR,
      showBarcode: effShowBarcode,
      qrDataUrl,
      barcodeDataUrl,
    });
    const inner = primitives.map(renderPrimString).join("");
    return `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${wPx} ${hPx}" width="${wMm}mm" height="${hMm}mm">
        <rect x="0" y="0" width="${wPx}" height="${hPx}" fill="#FFFFFF" />
        ${inner}
      </svg>
    `;
  }

  // -------- RING-TAG ZONED --------
  if (isRingTag(template)) {
    const { primitives } = ringTagPrimitives(product, template, language, {
      unit: 1, showQR: effShowQR, fontSize, qrDataUrl,
    });
    // NOTE: perforation lines intentionally omitted from the print output —
    // they're a preview-only visual aid; the paper's real die-cut lines up.
    const inner = primitives.map(renderPrimString).join("");
    return `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${wPx} ${hPx}" width="${wMm}mm" height="${hMm}mm">
        <rect x="0" y="0" width="${wPx}" height="${hPx}" fill="#FFFFFF" />
        ${inner}
      </svg>
    `;
  }

  // -------- GENERIC --------
  const padPx = 1.5 * MM_TO_PX;
  const headerFont = Math.round(fontSize * 1.4);
  const rowFont = Math.round(fontSize);
  const qrSizePx = Math.min(
    hPx - padPx * 3 - headerFont - rows.length * rowFont * 1.2,
    (wPx - padPx * 2) * 0.32,
  );

  const rowsXml = rows.map((row, idx) => {
    const y = padPx + headerFont * 1.25 + (idx + 1) * rowFont * 1.4;
    return `
      <text x="${padPx}" y="${y}" font-family="Manrope, Arial, sans-serif" font-size="${rowFont * 0.85}" font-weight="500" fill="#333333">${escapeXml(row.label)}:</text>
      <text x="${padPx + rowFont * 4.2}" y="${y}" font-family="Manrope, Arial, sans-serif" font-size="${rowFont}" font-weight="700" fill="#000000">${escapeXml(row.value)}</text>
    `;
  }).join("");

  const qrXml = effShowQR && qrDataUrl && !effShowBarcode
    ? `<image href="${qrDataUrl}" x="${wPx - padPx - qrSizePx}" y="${padPx + headerFont * 0.5}" width="${qrSizePx}" height="${qrSizePx}" />`
    : "";
  const barcodeXml = effShowBarcode && barcodeDataUrl
    ? `<g transform="translate(${padPx}, ${hPx - padPx - rowFont * 2.5})">${barcodeDataUrl}</g>`
    : "";

  return `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${wPx} ${hPx}" width="${wMm}mm" height="${hMm}mm">
      <rect x="0" y="0" width="${wPx}" height="${hPx}" fill="#FFFFFF" />
      <text x="${padPx}" y="${padPx + headerFont * 0.85}" font-family="Cormorant Garamond, Times New Roman, serif" font-size="${headerFont}" font-weight="700" letter-spacing="${headerFont * 0.06}" fill="#000000">${escapeXml(t.header)}</text>
      ${rowsXml}
      ${qrXml}
      ${barcodeXml}
    </svg>
  `;
}

function renderPrimString(p) {
  if (p.kind === "line") {
    return `<line x1="${p.x1}" y1="${p.y1}" x2="${p.x2}" y2="${p.y2}" stroke="${p.stroke}" stroke-width="${p.strokeWidth}" />`;
  }
  if (p.kind === "image") {
    if (!p.href) return "";
    return `<image href="${p.href}" x="${p.x}" y="${p.y}" width="${p.w}" height="${p.h}" />`;
  }
  if (p.kind === "barcode_group") {
    if (!p.inner) return "";
    return `<g transform="translate(${p.x}, ${p.y})">${p.inner}</g>`;
  }
  const ls = p.letterSpacing ? ` letter-spacing="${p.letterSpacing}"` : "";
  const anchor = p.anchor ? ` text-anchor="${p.anchor}"` : "";
  return `<text x="${p.x}" y="${p.y}"${anchor} font-family="${p.family}" font-size="${p.size}" font-weight="${p.weight || 400}"${ls} fill="${p.fill}">${escapeXml(p.text)}</text>`;
}

export default LabelPreview;
